import { useCallback, useState } from "react";
import { COLORS, TEXT_COLORS } from "../../constants/design/colors";
import { FONT_STYLE } from "../../constants/design/fontSize";
import { SPACING } from "../../constants/design/spacing";
import { Button, ButtonThemeEnum } from "../shared/Buttons/styles";
import ConfirmationModal from "../shared/ConfirmationModal";
import {
  OPTION_FEE_EXAMPLE,
  feeRateToBps,
  feeRateToPercent,
  formatFeeRate,
  formatPremiumCap,
  optionBuilderFeeExample,
  validateFeeRate,
  validateOptionPremiumCap,
} from "../../utils/builders";
import { errorStyle, inputStyle, panelStyle } from "./shared";

type OptionsField = "max_fee_rate_options" | "option_premium_cap";

const exampleText = (rate: string | null | undefined, premiumCap: string | null | undefined) => {
  const { indexPrice, contracts, premium } = OPTION_FEE_EXAMPLE;
  const fee = optionBuilderFeeExample(rate, premiumCap);
  const prefix = `ETH $${indexPrice.toLocaleString("en-US")}, ${contracts} contract, $${premium} premium`;
  if (fee === undefined) {
    return `${prefix} → no builder fee (options config NOT SET)`;
  }
  const partial = !rate || !premiumCap ? " (other setting NOT SET — options stay disabled)" : "";
  return `${prefix} → builder fee ≤ $${fee}${partial}`;
};

interface IFieldEditorProps {
  field: OptionsField;
  label: string;
  placeholder: string;
  buttonTitle: string;
  current: string | null | undefined;
  disabled: boolean;
  validate: (value: string) => string | undefined;
  format: (value: string) => string;
  confirmStats: (input: string) => { title: string; data: string }[];
  onSave: (value: string) => Promise<boolean>;
}

function FieldEditor({
  field, label, placeholder, buttonTitle, current, disabled, validate, format, confirmStats, onSave,
}: IFieldEditorProps) {
  const [input, setInput] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const inputError = input ? validate(input) : undefined;

  const onConfirm = useCallback(async () => {
    setSubmitting(true);
    const ok = await onSave(input);
    setSubmitting(false);
    if (ok) {
      setConfirming(false);
      setInput("");
    }
  }, [input, onSave]);

  return (
    <div style={{ marginBottom: SPACING.two }}>
      <div style={{ ...FONT_STYLE.label.two, color: TEXT_COLORS.two }}>{label}</div>
      <div
        style={{ color: current ? TEXT_COLORS.one : COLORS.red.one, fontWeight: 600, marginBottom: SPACING.one }}
        data-testid={`current-${field}`}
      >
        {current ? `${format(current)} (${current})` : "NOT SET"}
      </div>
      <div style={{ display: "flex", gap: SPACING.two, alignItems: "flex-start" }}>
        <div style={{ width: 240 }}>
          <input
            name={field}
            placeholder={placeholder}
            value={input}
            onChange={(e) => setInput(e.target.value.trim())}
            style={inputStyle}
            disabled={disabled}
            aria-label={`New ${label}`}
          />
          {inputError && <div style={errorStyle}>{inputError}</div>}
          {input && !inputError && (
            <div style={{ ...FONT_STYLE.label.three, color: TEXT_COLORS.two, marginTop: SPACING.one }}>
              = {format(input)}
            </div>
          )}
        </div>
        <Button
          buttonTheme={ButtonThemeEnum.NEUTRAL2}
          disabled={!input || !!inputError || disabled}
          onClick={() => setConfirming(true)}
          style={{ height: 32, padding: `0 ${SPACING.two}px` }}
        >
          {buttonTitle}
        </Button>
      </div>
      <ConfirmationModal
        show={confirming}
        onHide={() => setConfirming(false)}
        title={`Confirm ${label}`}
        primaryColor={COLORS.orange.one}
        width="460px"
        minWidth="460px"
        isLoading={submitting}
        stats={input ? confirmStats(input) : []}
        warning="Applies to builder fees on OPTION orders only. Other builder settings are unchanged."
        confirmationButton={{
          title: buttonTitle,
          onClick: onConfirm,
          disabled: !input || !!inputError,
        }}
      />
    </div>
  );
}

interface IOptionsFeePanelProps {
  maxFeeRateOptions: string | null | undefined;
  optionPremiumCap: string | null | undefined;
  isLoading: boolean;
  disabled: boolean;
  onSaveRate: (rate: string) => Promise<boolean>;
  onSavePremiumCap: (cap: string) => Promise<boolean>;
}

/**
 * Options builder fees: fee = min(rate x index x contracts,
 * premiumCap x premium x contracts). Fail closed until BOTH are set.
 */
export function OptionsFeePanel({
  maxFeeRateOptions, optionPremiumCap, isLoading, disabled, onSaveRate, onSavePremiumCap,
}: IOptionsFeePanelProps) {
  const isUnset = !isLoading && !disabled && (!maxFeeRateOptions || !optionPremiumCap);

  let status: JSX.Element | undefined;
  if (isLoading) {
    status = <span style={{ color: TEXT_COLORS.two }}>Loading…</span>;
  } else if (disabled) {
    status = <span style={{ color: TEXT_COLORS.two }}>Unavailable</span>;
  } else if (isUnset) {
    status = (
      <span style={{ color: COLORS.red.one, fontWeight: 700 }}>
        NOT SET — builder fees on options disabled
      </span>
    );
  }

  return (
    <div
      style={{
        ...panelStyle,
        flex: 1,
        minWidth: 360,
        background: isUnset ? COLORS.red.five : panelStyle.background,
      }}
    >
      <div style={{ ...FONT_STYLE.header.six, marginBottom: SPACING.two }}>
        Options builder fees
      </div>
      {status && <div style={{ marginBottom: SPACING.two }}>{status}</div>}
      <div style={{ ...FONT_STYLE.label.two, color: TEXT_COLORS.two, marginBottom: SPACING.two }}>
        Builder fee per option fill = min(rate × index × contracts, premium cap × premium × contracts).
        Both settings must be set; one approval covers perps and options.
      </div>
      {!isLoading && !disabled && (
        <div
          style={{ ...FONT_STYLE.label.two, color: TEXT_COLORS.one, marginBottom: SPACING.two }}
          data-testid="options-example"
        >
          {exampleText(maxFeeRateOptions, optionPremiumCap)}
        </div>
      )}
      <FieldEditor
        field="max_fee_rate_options"
        label="options max builder fee rate"
        placeholder="e.g. 0.0003 (= 3 bps of index)"
        buttonTitle="Set options rate"
        current={maxFeeRateOptions}
        disabled={disabled || isLoading}
        validate={validateFeeRate}
        format={formatFeeRate}
        confirmStats={(input) => [
          { title: "Current", data: maxFeeRateOptions ? formatFeeRate(maxFeeRateOptions) : "NOT SET" },
          { title: "New", data: formatFeeRate(input) },
          { title: "New (bps)", data: `${feeRateToBps(input)} bps` },
          { title: "Example", data: exampleText(input, optionPremiumCap) },
        ]}
        onSave={onSaveRate}
      />
      <FieldEditor
        field="option_premium_cap"
        label="option premium cap"
        placeholder="e.g. 0.05 (= 5% of premium)"
        buttonTitle="Set premium cap"
        current={optionPremiumCap}
        disabled={disabled || isLoading}
        validate={validateOptionPremiumCap}
        format={formatPremiumCap}
        confirmStats={(input) => [
          { title: "Current", data: optionPremiumCap ? formatPremiumCap(optionPremiumCap) : "NOT SET" },
          { title: "New", data: formatPremiumCap(input) },
          { title: "New (%)", data: `${feeRateToPercent(input)}%` },
          { title: "Example", data: exampleText(maxFeeRateOptions, input) },
        ]}
        onSave={onSavePremiumCap}
      />
    </div>
  );
}
